"use client";

import { Fragment, useState, type ReactNode } from "react";
import Link from "next/link";
import { ArrowRight, Check, Copy } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ContactFormDialog } from "@/components/contact-form-dialog";
import {
  goodToKnow,
  plans,
  products,
  type Plan,
  type Product,
} from "@/lib/pricing-data";

// Shared style fragments
const serif = "font-[Source_Serif_4,Georgia,serif]";
const section =
  "mx-auto max-w-[940px] px-8 max-md:px-5";
const sectionHeading = cn(
  serif,
  "font-medium tracking-[-1px] text-[38px] leading-[44px] text-balance",
);
// Cyan brand fill — `primary` is the Moondream brand color via .brand-moondream.
const brandPrimary =
  "bg-primary text-primary-foreground shadow-none hover:bg-primary/90";
const brandInk =
  "bg-foreground text-background shadow-none hover:bg-foreground/90";
const outlineBtn =
  "shadow-none hover:bg-transparent hover:text-foreground hover:border-muted-foreground dark:bg-transparent dark:hover:bg-transparent";

const freeTag = "bg-primary/10 text-foreground";

/** Renders **bold** spans as foreground-weight text; everything else stays inline. */
function renderEmphasis(text: string): ReactNode[] {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-foreground">
        {part}
      </strong>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}

const InlineCommand = ({ command }: { command: string }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  return (
    <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 font-[family-name:var(--font-geist-mono)] text-[13px] leading-none">
      <span className="select-none text-muted-foreground">$</span>
      <span className="flex-1 truncate">{command}</span>
      <button
        type="button"
        onClick={copy}
        aria-label="Copy command"
        className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
      >
        {copied ? (
          <Check className="size-3.5 text-primary" strokeWidth={2} />
        ) : (
          <Copy className="size-3.5" strokeWidth={1.75} />
        )}
      </button>
    </div>
  );
};

const ProductCard = ({ product }: { product: Product }) => (
  <div className="group flex flex-col gap-3.5 rounded-xl border bg-card p-[26px] transition-colors duration-200 hover:border-muted-foreground">
    <div className="flex items-center justify-between gap-3">
      <div className="text-[20px] font-semibold leading-[26px] tracking-[-0.01em]">
        {product.name}
      </div>
      {product.tag === "free" ? (
        <span
          className={cn(
            "inline-flex h-[26px] items-center whitespace-nowrap rounded-full px-3 text-[13px] font-semibold",
            freeTag,
          )}
        >
          Free
        </span>
      ) : (
        <span className="inline-flex h-[26px] items-center whitespace-nowrap rounded-full border px-3 text-[13px] font-semibold text-muted-foreground">
          Pay per token
        </span>
      )}
    </div>

    <p className="text-[15px] leading-[23px] text-muted-foreground">
      {product.copy}
    </p>

    {product.install && <InlineCommand command={product.install} />}

    {product.downloads && (
      <p className="text-[13px] leading-[20px] text-muted-foreground">
        ..or click here if you really want to download them:{" "}
        {product.downloads.map((download, i) => (
          <Fragment key={download.href}>
            {i > 0 && " · "}
            <a
              href={download.href}
              target="_blank"
              rel="noreferrer"
              className="text-foreground underline underline-offset-2 transition-colors hover:text-primary"
            >
              {download.label}
            </a>
          </Fragment>
        ))}
      </p>
    )}

    {product.detail && (
      <div className="mt-0.5 flex flex-col gap-3.5 border-t pt-4">
        {product.detail.map((line) => (
          <div key={line.label}>
            <div className="mb-1.5 text-[12px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              {line.label}
            </div>
            {line.rows.map((row, i) => (
              <div
                key={i}
                className="whitespace-nowrap font-[family-name:var(--font-geist-mono)] text-[14px] leading-[22px] tabular-nums"
              >
                {row.num && (
                  <span className="font-medium text-foreground">{row.num} </span>
                )}
                <span className="text-muted-foreground">{row.unit}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    )}

    {product.cta && (
      <div className="mt-auto pt-1.5">
        {product.cta.variant === "link" ? (
          <Link
            href={product.cta.href}
            className="inline-flex items-center gap-1.5 text-[14px] font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {product.cta.text}
            <ArrowRight className="size-[15px]" strokeWidth={1.75} />
          </Link>
        ) : (
          <Button size="sm" variant="outline" className={outlineBtn} asChild>
            <Link href={product.cta.href}>{product.cta.text}</Link>
          </Button>
        )}
      </div>
    )}
  </div>
);

const PlanCard = ({
  plan,
  onContact,
}: {
  plan: Plan;
  onContact: () => void;
}) => {
  const ctaClass = cn(
    "w-full",
    plan.cta.variant === "primary" && brandPrimary,
    plan.cta.variant === "ink" && brandInk,
    plan.cta.variant === "outline" && outlineBtn,
  );

  const ctaButton = plan.cta.href ? (
    <Button
      variant={plan.cta.variant === "outline" ? "outline" : "default"}
      className={ctaClass}
      asChild
    >
      <Link href={plan.cta.href}>{plan.cta.text}</Link>
    </Button>
  ) : (
    <Button
      variant={plan.cta.variant === "outline" ? "outline" : "default"}
      className={ctaClass}
      onClick={onContact}
    >
      {plan.cta.text}
    </Button>
  );

  return (
    <div
      className={cn(
        "relative flex flex-col gap-4 rounded-xl border bg-card px-6 py-[26px]",
        plan.featured &&
          "border-primary shadow-[0_0_0_1px_var(--primary)] max-[860px]:order-[-1]",
      )}
    >
      {plan.featured && (
        <span className="absolute -top-[11px] left-6 inline-flex h-[22px] items-center rounded-full bg-primary px-2.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-primary-foreground">
          Recommended
        </span>
      )}

      <div className="text-[17px] font-semibold leading-[22px] tracking-[-0.01em]">
        {plan.name}
      </div>

      <div className="flex flex-wrap items-baseline gap-[7px]">
        <span className="text-[34px] font-semibold leading-[38px] tracking-[-0.02em] tabular-nums">
          {plan.amount}
        </span>
        {plan.per && (
          <span className="text-[14px] text-muted-foreground">{plan.per}</span>
        )}
      </div>

      <div className="-mt-2 min-h-[18px] text-[13px] text-muted-foreground">
        {plan.annualNote ? renderEmphasis(plan.annualNote) : " "}
      </div>

      <div className="text-[13px] leading-[20px] text-muted-foreground">
        {renderEmphasis(plan.bestFor)}
      </div>

      {ctaButton}

      <ul className="flex flex-1 flex-col gap-[9px]">
        {plan.features.map((feature) => (
          <li
            key={feature.label}
            className={cn(
              "flex items-start gap-[9px] text-[14px] leading-5",
              feature.strong
                ? "font-semibold text-foreground"
                : "text-card-foreground",
            )}
          >
            <Check
              className="mt-0.5 size-4 shrink-0 text-primary"
              strokeWidth={2}
            />
            {feature.label}
          </li>
        ))}
      </ul>

      {plan.seatNote && (
        <div className="text-[12px] text-muted-foreground">{plan.seatNote}</div>
      )}
    </div>
  );
};

const SectionDivider = () => (
  <hr className="border-0 border-t border-border" />
);

const PricingContent = () => {
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <div className="brand-moondream bg-background text-foreground">
      {/* 1 · Hero */}
      <section
        className={cn(section, "pb-2 pt-[72px] text-center")}
      >
        <div className="mx-auto flex max-w-[720px] flex-col items-center gap-[22px]">
          <h1
            className={cn(
              serif,
              "text-balance text-[60px] font-medium leading-[66px] tracking-[-1.6px] max-md:text-[42px] max-md:leading-[48px] max-md:tracking-[-1.2px]",
            )}
          >
            Simple pricing for{" "}
            <em className="italic text-primary">Moondream.</em>
          </h1>
          <p className="max-w-[600px] text-pretty text-[18px] leading-[29px] text-muted-foreground">
            Models and Photon are free. Cloud and Lens are pay-per-token. Paid
            plans add collaboration and support.
          </p>
          <div className="mt-1 flex flex-wrap items-center justify-center gap-3">
            <Button size="lg" className={brandPrimary} asChild>
              <Link href="/login">Start free</Link>
            </Button>
            <Button size="lg" variant="outline" className={outlineBtn} asChild>
              <Link href="/org/me/create-organization">Start Team</Link>
            </Button>
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="inline-flex items-center gap-1.5 text-[14px] font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Talk to sales
              <ArrowRight className="size-[15px]" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </section>

      {/* 2 · Products */}
      <section className={cn(section, "py-20 max-md:py-14")}>
        <h2 className={cn(sectionHeading, "mb-9")}>Pick what you need.</h2>
        <div className="grid grid-cols-2 items-stretch gap-[18px] max-md:grid-cols-1">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </section>

      <SectionDivider />

      {/* 3 · Plans */}
      <section className={cn(section, "py-20 max-md:py-14")}>
        <div className="mb-9">
          <h2 className={sectionHeading}>Need collaboration or support?</h2>
          <p className="mt-3 max-w-[560px] text-pretty text-[16px] leading-[26px] text-muted-foreground">
            Paid plans add seats, shared resources, billing, and direct support.
            They do not change token pricing.
          </p>
        </div>
        <div className="grid grid-cols-3 items-start gap-[18px] max-[860px]:grid-cols-1">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onContact={() => setContactOpen(true)}
            />
          ))}
        </div>
        <p className="mt-4 text-[14px] leading-[22px] text-muted-foreground">
          Paid plans do not change Cloud or Lens token pricing.
        </p>
      </section>

      <SectionDivider />

      {/* 4 · Good to know */}
      <section className={cn(section, "py-20 max-md:py-14")}>
        <div className="rounded-xl border bg-card px-8 py-[30px]">
          <h3 className="mb-[18px] text-[16px] font-semibold leading-[22px]">
            Good to know
          </h3>
          <ul className="grid grid-cols-2 gap-x-8 gap-y-3.5 max-[640px]:grid-cols-1">
            {goodToKnow.map((item, i) => (
              <li
                key={i}
                className="flex gap-[11px] text-[14.5px] leading-[22px] text-muted-foreground"
              >
                <Check
                  className="mt-0.5 size-[17px] shrink-0 text-primary"
                  strokeWidth={1.75}
                />
                <span>{renderEmphasis(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <ContactFormDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
};

export { PricingContent };
